import { Card } from '@design-system/Card';
import { Link } from '@design-system/Link';
import { Button } from '@design-system/Button';
import { TRACK_SAMPLE_STATUS_PATH } from '@/constants/paths';
import { Skeleton } from '@design-system/Skeleton';
import { List, ListItem } from '@design-system/List';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { useGetInitData } from '@viome/viome-base/src/usecase/Bootup/BootupQuery';
import { getKitName } from '@utils/getKitName';
import { useNavigate } from 'react-router-dom';
import * as P from '@/constants/paths';
import { useCancerDetectUser } from '@hooks/useCancerDetectUser';
import { linkoutConfigs } from '@config/routing/LinkoutConfig';

const TEST_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
} as const;

export type ITestStatus = (typeof TEST_STATUS)[keyof typeof TEST_STATUS];

export interface ITest {
  id: string;
  name: string;
  imageSrc: string;
  description: string;
  status: ITestStatus;
}

export interface AccountManagementPageTestsSectionProps
  extends React.ComponentProps<'div'> {
  tests?: ITest[];
  isLoading?: boolean;
}

const renderTests = (tests?: ITest[], isLoading?: boolean) => {
  if (isLoading) {
    return Array.from({ length: 3 }).map((_, index) => (
      <ListItem key={index} hideIndicator>
        <Skeleton className="w-full h-4 max-w-64" />
        <Skeleton className="ml-auto w-full h-4 max-w-24" />
      </ListItem>
    ));
  }

  if (!tests?.length) return null;

  const renderedTests = tests.map((test) => (
    <ListItem key={test.id} hideIndicator>
      <abbr className="no-underline" title={test.name}>
        <span className="truncate text-primary">{test.name}</span>
      </abbr>

      <span className="text-tertiary ml-auto">{test.status}</span>
    </ListItem>
  ));

  return renderedTests;
};

export const AccountManagementPageTestsSection = ({
  tests,
  isLoading: testsLoading,
}: AccountManagementPageTestsSectionProps) => {
  const { data: initData, isLoading: initDataLoading } = useGetInitData();
  const { isCancerDetectClicked } = useCancerDetectUser();

  const isLoading = testsLoading || initDataLoading;

  const wellnessKitType = initData?.lockstatus?.hasRecommendation
    ? initData?.kitDetails?.kitType
    : initData?.kitDetails?.pre_rnr_kit_type;

  const cancerDetectKitType = initData?.CDkitDetails?.kitType || initData?.CDkitDetails?.pre_rnr_kit_type;

  const activeKitType = isCancerDetectClicked ? cancerDetectKitType : wellnessKitType;

  const kitName = getKitName(activeKitType);

  const navigate = useNavigate();
  return (
    <div className="flex flex-col">
      <span className="typography-h2 text-primary mb-6">My Tests</span>

      {isLoading ? (
        <Card className="mb-2 overflow-hidden">
          <List>
            <ListItem hideIndicator>
              <Skeleton className="w-full h-4 max-w-64" />
              <Skeleton className="ml-auto w-full h-4 max-w-24" />
            </ListItem>
          </List>
        </Card>
      ) : kitName ? (
        <Card className="mb-2 overflow-hidden">
          <List>
            <ListItem
              hideIndicator
              className="flex justify-between items-center"
            >
              <span className="text-primary">{kitName}</span>
              <span className="text-secondary">Active</span>
            </ListItem>
          </List>
        </Card>
      ) : null}

      <Card className="mb-2 overflow-hidden">
        <List>
          {renderTests(tests, testsLoading)}
          <ListItem asChild>
            <Link to={TRACK_SAMPLE_STATUS_PATH}>See all tests</Link>
          </ListItem>
        </List>
      </Card>

      <Button
        className="[&_svg]:!size-6 justify-start"
        onClick={() => navigate(P.REGISTRATION_KIT_ID)}
      >
        Register a new test
        <ChevronRightIcon
          width={24}
          height={24}
          aria-hidden
          className="text-secondary-foreground ml-auto"
        />
      </Button>

      <Card className="mt-4 overflow-hidden">
        <List>
          <ListItem asChild>
            <Link to={linkoutConfigs.writeReview}>Write a Review</Link>
          </ListItem>
        </List>
      </Card>

    </div>
  );
};
